import type {
  PublishedProject,
  PublicOverviewProject,
  ReadmeProject,
} from "./types";

const PROJECT_ID_PATTERN = /^[a-z0-9]+(?:[-_][a-z0-9]+)*$/;
const ISO_DATE_PATTERN = /^\d{4}-\d{2}-\d{2}$/;
const STABLE_VERSION_PATTERN = /^\d+\.\d+$/;

/** Validates one generated project projection, including stable lower-case hyphen/underscore project IDs, before it can enter the browser catalog. */
export function assertValidPublishedProject(project: PublishedProject): void {
  if (
    project.sourceType !== "public-overview" &&
    project.sourceType !== "readme"
  ) {
    throw new Error(
      'Project sourceType must be "public-overview" or "readme".',
    );
  }

  if (!PROJECT_ID_PATTERN.test(project.id)) {
    throw new Error(
      `Project id "${project.id}" must be lower-case and use only letters, numbers, hyphens, or underscores.`,
    );
  }

  if (
    project.sourceVisibility === "private" &&
    project.repositoryUrl !== null
  ) {
    throw new Error(
      `Private project "${project.id}" must not expose a repository URL.`,
    );
  }

  if (project.sourceVisibility === "public") {
    assertPublicRepositoryUrl(project);
  }

  if (project.sourceType === "public-overview") {
    assertValidPublicOverviewProject(project);
  } else {
    assertValidReadmeProject(project);
  }
}

/** Validates the stable-release identity and narrative required by a public overview. */
function assertValidPublicOverviewProject(
  project: PublicOverviewProject,
): void {
  if (!STABLE_VERSION_PATTERN.test(project.version)) {
    throw new Error(
      `Project "${project.id}" version must use the stable X.Y format.`,
    );
  }

  if (project.sourceRef !== `v${project.version}`) {
    throw new Error(
      `Project "${project.id}" sourceRef must equal v${project.version}.`,
    );
  }

  if (!ISO_DATE_PATTERN.test(project.releaseDate)) {
    throw new Error(`Project "${project.id}" has an invalid releaseDate.`);
  }

  assertRequiredNarrative(project);
}

/** Validates README presentation metadata, optional display order and engineering highlights, and sanitized Markdown before publication. */
function assertValidReadmeProject(project: ReadmeProject): void {
  if (!project.summary.trim()) {
    throw new Error(`README project "${project.id}" requires a summary.`);
  }
  if (!project.label.trim()) {
    throw new Error(`README project "${project.id}" requires a label.`);
  }
  if (
    project.displayOrder !== undefined &&
    (!Number.isInteger(project.displayOrder) || project.displayOrder < 0)
  ) {
    throw new Error(
      `README project "${project.id}" displayOrder must be a non-negative integer.`,
    );
  }
  if (project.portfolioHighlights !== undefined) {
    if (
      project.portfolioHighlights.length === 0 ||
      project.portfolioHighlights.length > 6 ||
      project.portfolioHighlights.some(
        (highlight) => highlight.trim().length === 0,
      )
    ) {
      throw new Error(
        `README project "${project.id}" portfolioHighlights must contain one to six non-empty strings.`,
      );
    }
  }
  if (!project.markdown.trim()) {
    throw new Error(
      `README project "${project.id}" requires Markdown content.`,
    );
  }
}

/** Requires a public project link to be an HTTPS GitHub repository URL. */
function assertPublicRepositoryUrl(project: PublishedProject): void {
  if (!project.repositoryUrl) {
    throw new Error(
      `Public project "${project.id}" requires a repository URL.`,
    );
  }

  let url: URL;
  try {
    url = new URL(project.repositoryUrl);
  } catch {
    throw new Error(`Project "${project.id}" has an invalid repository URL.`);
  }

  if (url.protocol !== "https:" || url.hostname !== "github.com") {
    throw new Error(
      `Project "${project.id}" repository URL must be an HTTPS github.com URL.`,
    );
  }
}

/** Requires all six narrative sections defined by the stable public-overview design-standard contract. */
function assertRequiredNarrative(project: PublicOverviewProject): void {
  const sections: Array<[string, string[]]> = [
    ["purpose", project.purpose],
    ["whatItDoes", project.whatItDoes],
    ["howItWorks", project.howItWorks],
    ["releaseHighlights", project.releaseHighlights],
    ["currentLimitations", project.currentLimitations],
    ["validation", project.validation],
  ];

  for (const [name, values] of sections) {
    if (
      values.length === 0 ||
      values.some((value) => value.trim().length === 0)
    ) {
      throw new Error(`Project "${project.id}" requires non-empty ${name}.`);
    }
  }
}
